






















































































































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import TaskKanbanBoard from './TaskKanbanBoard.vue';
import TaskKanbanItem from './TaskKanbanItem.vue';
import { ITask, ReorderTasksDto, TaskStatus } from 'admiral-core';
import { BaseMixin } from '@/mixins/BaseMixin';
import { mixins } from 'vue-class-component';
import draggable from 'vuedraggable';
import { TaskApi } from '@/services/TaskApi';
import {
  buildFullOrderedIdsForStatus,
  compareTasksByStatusOrder,
  getDraggedTaskIdFromSortableItem,
  resolveListStatus,
} from '@/utils/taskListOrder';
import TaskSectionSortMenu from '@/components/tasks/TaskSectionSortMenu.vue';
import type { SectionSortField } from '@/components/tasks/SectionSortField';
import { ArrowDownIcon, ArrowUpIcon } from 'vue-feather-icons';

@Component({
  name: 'Tasks2Kanban',
  components: {
    TaskKanbanBoard,
    TaskKanbanItem,
    TaskSectionSortMenu,
    ArrowDownIcon,
    ArrowUpIcon,
    draggable,
  },
})
export default class TaskKanban extends mixins(BaseMixin) {
  @Prop({ required: true, type: Array })
  tasks!: ITask[];

  @Prop({ type: Array, required: false })
  tasksForReorder?: ITask[];

  @Prop({
    type: Object,
    required: false,
    default: () => ({ showDemo: false }),
  })
  taskReorderContext?: Pick<
    ReorderTasksDto,
    | 'showDemo'
    | 'tags'
    | 'users'
    | 'priority'
    | 'backoffice'
    | 'important'
    | 'completedListLimit'
    | 'archivedListLimit'
  >;

  @Prop({ type: Boolean, default: false })
  showLoadMoreCompleted!: boolean;

  @Prop({ type: Boolean, default: false })
  loadingMoreCompleted!: boolean;

  @Prop({ type: Boolean, default: false })
  showLoadMoreArchived!: boolean;

  @Prop({ type: Boolean, default: false })
  loadingMoreArchived!: boolean;

  /** When true, boards are hidden and a loading state is shown (tasks2 initial / full refetch). */
  @Prop({ type: Boolean, default: false })
  loading!: boolean;

  /**
   * Max COMPLETED cards shown (same as API `completedListLimit` from parent).
   * Other columns are unchanged.
   */
  @Prop({ type: Number, required: false })
  completedMaxDisplay?: number;

  TaskStatus = TaskStatus;

  sectionSortField: Record<TaskStatus, SectionSortField> = {
    [TaskStatus.TODO]: 'manual',
    [TaskStatus.IN_PROGRESS]: 'manual',
    [TaskStatus.IN_WAITING]: 'manual',
    [TaskStatus.COMPLETED]: 'manual',
    [TaskStatus.ARCHIVED]: 'manual',
  };

  sectionSortDesc: Record<TaskStatus, boolean> = {
    [TaskStatus.TODO]: true,
    [TaskStatus.IN_PROGRESS]: true,
    [TaskStatus.IN_WAITING]: true,
    [TaskStatus.COMPLETED]: true,
    [TaskStatus.ARCHIVED]: true,
  };

  listByStatus: Record<TaskStatus, ITask[]> = {
    [TaskStatus.TODO]: [],
    [TaskStatus.IN_PROGRESS]: [],
    [TaskStatus.IN_WAITING]: [],
    [TaskStatus.COMPLETED]: [],
    [TaskStatus.ARCHIVED]: [],
  };

  @Watch('tasks', { immediate: true })
  onTasksPropUpdated(tasks: ITask[]) {
    const source = Array.isArray(tasks) ? tasks : [];
    this.listByStatus = this.buildGroupedTasksFromTasks(source);
    this.applySectionSorts();
  }

  isManualSort(section: TaskStatus): boolean {
    return this.sectionSortField[section] === 'manual';
  }

  /**
   * Sortable `move` hook (vuedraggable): allow horizontal moves to another status column
   * even when the column uses date sorting; only block reordering within the same column
   * unless sort is "Manuell sortiert".
   */
  kanbanDragMove(evt: { from?: HTMLElement; to?: HTMLElement }): boolean {
    const from = evt.from;
    const to = evt.to;
    if (from && to && from === to) {
      const status = resolveListStatus(from);
      if (status != null && !this.isManualSort(status)) {
        return false;
      }
    }
    return true;
  }

  setSectionSortField(section: TaskStatus, field: SectionSortField) {
    const previous = this.sectionSortField[section];
    if (field !== 'manual' && previous !== field) {
      if (field === 'dueAt') this.sectionSortDesc[section] = false;
      else this.sectionSortDesc[section] = true;
    }
    this.sectionSortField[section] = field;
    this.applySort(section);
  }

  toggleSortDirection(section: TaskStatus) {
    this.sectionSortDesc[section] = !this.sectionSortDesc[section];
    this.applySort(section);
  }

  sortDirectionTooltip(section: TaskStatus): string {
    const field = this.sectionSortField[section];
    const desc = this.sectionSortDesc[section];
    if (field === 'createdAt') {
      return desc ? 'Neueste zuerst' : 'Älteste zuerst';
    }
    if (field === 'updatedAt') {
      return desc ? 'Zuletzt bearbeitet zuerst' : 'Am längsten unverändert zuerst';
    }
    if (field === 'dueAt') {
      return desc ? 'Späteste Fälligkeit zuerst' : 'Früheste Fälligkeit zuerst';
    }
    return '';
  }

  private applySectionSorts() {
    this.applySort(TaskStatus.TODO);
    this.applySort(TaskStatus.IN_PROGRESS);
    this.applySort(TaskStatus.IN_WAITING);
    this.applySort(TaskStatus.COMPLETED);
    this.applySort(TaskStatus.ARCHIVED);
  }

  private applySort(section: TaskStatus) {
    const sectionList = this.listByStatus[section];
    if (!sectionList) return;

    const field = this.sectionSortField[section];
    const desc = this.sectionSortDesc[section];

    if (field === 'manual') {
      sectionList.sort(compareTasksByStatusOrder);
      return;
    }

    if (field === 'createdAt') {
      sectionList.sort((a, b) => {
        const cmp = this.taskTs(a.createdAt) - this.taskTs(b.createdAt);
        return desc ? -cmp : cmp;
      });
      return;
    }

    if (field === 'updatedAt') {
      sectionList.sort((a, b) => {
        const cmp = this.taskTs(a.updatedAt) - this.taskTs(b.updatedAt);
        return desc ? -cmp : cmp;
      });
      return;
    }

    if (field === 'dueAt') {
      sectionList.sort((a, b) => {
        const aTs = a.dueAt
          ? this.taskTs(a.dueAt)
          : desc
          ? Number.NEGATIVE_INFINITY
          : Number.POSITIVE_INFINITY;
        const bTs = b.dueAt
          ? this.taskTs(b.dueAt)
          : desc
          ? Number.NEGATIVE_INFINITY
          : Number.POSITIVE_INFINITY;
        if (aTs !== bTs) return desc ? bTs - aTs : aTs - bTs;
        return this.taskTs(b.updatedAt) - this.taskTs(a.updatedAt);
      });
      return;
    }

    sectionList.sort(compareTasksByStatusOrder);
  }

  private taskTs(value: string | Date | undefined | null): number {
    if (value == null || value === '') return 0;
    const t = new Date(value as string).getTime();
    return Number.isNaN(t) ? 0 : t;
  }

  buildGroupedTasksFromTasks(tasks: ITask[]): Record<TaskStatus, ITask[]> {
    const groups: Record<TaskStatus, ITask[]> = {
      [TaskStatus.TODO]: [],
      [TaskStatus.IN_PROGRESS]: [],
      [TaskStatus.IN_WAITING]: [],
      [TaskStatus.COMPLETED]: [],
      [TaskStatus.ARCHIVED]: [],
    };
    const validStatuses = new Set(Object.values(TaskStatus));
    for (const t of tasks) {
      const status = t.status && validStatuses.has(t.status) ? t.status : TaskStatus.TODO;
      groups[status].push(t);
    }
    for (const arr of Object.values(groups)) {
      if (arr) arr.sort(compareTasksByStatusOrder);
    }
    return groups;
  }

  get reorderBasis(): ITask[] {
    return this.tasksForReorder != null ? this.tasksForReorder : this.tasks;
  }

  patchSortOrders(status: TaskStatus, orderedIds: string[]) {
    orderedIds.forEach((id, idx) => {
      const t = this.reorderBasis.find((x) => x.id === id);
      if (t && t.status === status) {
        this.$set(t, 'statusSortOrder', idx);
      }
    });
  }

  async onDragEnd(evt: any) {
    if (evt.oldDraggableIndex === evt.newDraggableIndex && evt.from === evt.to) return;

    const movedId = getDraggedTaskIdFromSortableItem(evt.item as HTMLElement);
    if (!movedId) return;

    const moved = this.reorderBasis.find((t) => t.id === movedId);
    if (!moved) return;

    const fromEl = evt.from as HTMLElement;
    const toEl = evt.to as HTMLElement;
    const newStatus = resolveListStatus(toEl);
    if (!newStatus) return;

    const previousStatus = moved.status;
    this.$set(moved, 'status', newStatus);
    await this.$nextTick();

    try {
      if (previousStatus !== newStatus) {
        await TaskApi.updateStatus(moved.id, moved, newStatus);
      }

      const agency = this.user.agency;
      const toOrder = buildFullOrderedIdsForStatus(newStatus, toEl, this.reorderBasis);
      await TaskApi.reorder({
        agency,
        status: newStatus,
        orderedTaskIds: toOrder,
        ...(this.taskReorderContext || {}),
      });
      this.patchSortOrders(newStatus, toOrder);

      if (previousStatus !== newStatus) {
        const fromOrder = buildFullOrderedIdsForStatus(previousStatus, fromEl, this.reorderBasis);
        await TaskApi.reorder({
          agency,
          status: previousStatus,
          orderedTaskIds: fromOrder,
          ...(this.taskReorderContext || {}),
        });
        this.patchSortOrders(previousStatus, fromOrder);
      }

      this.$emit('reload');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Task drag/reorder failed', e);
      this.$toastr.e('Verschieben fehlgeschlagen');
      this.$emit('reload');
    }
  }

  openTask(task: ITask) {
    this.$emit('open-task', task);
  }
}
